import React, { useState } from 'react';
import { MarketAlertItem } from '../shared/types';
import { X, AlertCircle, AlertTriangle, Info, CheckCircle2, ShieldAlert } from 'lucide-react';

interface AlertsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  alerts: MarketAlertItem[];
  onToggleResolve: (id: string) => void;
}

export const AlertsDrawer: React.FC<AlertsDrawerProps> = ({
  isOpen,
  onClose,
  alerts,
  onToggleResolve,
}) => {
  const [filterSeverity, setFilterSeverity] = useState<string>('ALL');

  if (!isOpen) return null;

  const filteredAlerts = alerts.filter((a) => {
    if (filterSeverity === 'ALL') return true;
    return a.severity === filterSeverity;
  });

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 50,
      display: 'flex',
      justifyContent: 'flex-end',
      background: 'rgba(0, 0, 0, 0.65)',
      backdropFilter: 'blur(4px)',
    }}>
      {/* Drawer Container */}
      <div style={{
        width: '100%',
        maxWidth: '480px',
        height: '100%',
        background: 'var(--bg-surface-elevated)',
        borderLeft: '1px solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'var(--shadow-drawer)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShieldAlert size={20} color="var(--color-warning)" />
            <h3 style={{ fontSize: '1.125rem', fontWeight: 700 }}>系统健康与运行警报</h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '6px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Severity Filter Tabs */}
        <div style={{
          padding: '12px 24px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex',
          gap: '8px',
          fontSize: '0.75rem',
        }}>
          {['ALL', 'CRITICAL', 'HIGH', 'WARNING', 'INFO'].map((sev) => (
            <button
              key={sev}
              onClick={() => setFilterSeverity(sev)}
              style={{
                padding: '4px 10px',
                borderRadius: '6px',
                border: filterSeverity === sev ? '1px solid var(--accent-cyan)' : '1px solid var(--border-subtle)',
                background: filterSeverity === sev ? 'var(--accent-cyan-glow)' : 'transparent',
                color: filterSeverity === sev ? 'var(--accent-cyan)' : 'var(--text-muted)',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {sev}
            </button>
          ))}
        </div>

        {/* Alerts List */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          {filteredAlerts.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: '48px', fontSize: '0.875rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🛡️</div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>系统运行正常，无活跃警报</div>
              <div style={{ fontSize: '0.75rem', marginTop: '6px', opacity: 0.7 }}>
                所有底层时序采集管道、时钟同步及多源容灾通道处于稳健状态。
              </div>
            </div>
          ) : (
            filteredAlerts.map((alert) => {
              const isCrit = alert.severity === 'CRITICAL';
              const isHigh = alert.severity === 'HIGH';
              const isWarn = alert.severity === 'WARNING';
              return (
                <div
                  key={alert.id}
                  className="glass-panel"
                  style={{
                    padding: '14px',
                    opacity: alert.resolved ? 0.5 : 1,
                    borderLeft: `4px solid ${
                      isCrit ? 'var(--color-bearish)' : isHigh ? 'var(--color-warning)' : isWarn ? '#eab308' : 'var(--accent-cyan)'
                    }`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {isCrit && <AlertCircle size={16} color="var(--color-bearish)" />}
                      {isHigh && <AlertTriangle size={16} color="var(--color-warning)" />}
                      {isWarn && <AlertTriangle size={16} color="#eab308" />}
                      {!isCrit && !isHigh && !isWarn && <Info size={16} color="var(--accent-cyan)" />}
                      <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{alert.title}</span>
                    </div>
                    <span className="badge" style={{
                      fontSize: '0.65rem',
                      background: alert.resolved ? 'var(--tag-inactive-bg)' : 'var(--bg-surface-hover)',
                    }}>
                      {alert.domain}
                    </span>
                  </div>

                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '6px', lineHeight: 1.4 }}>
                    {alert.message}
                  </p>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '10px',
                    fontSize: '0.75rem',
                    color: 'var(--text-muted)',
                  }}>
                    <span className="mono">{alert.timestampUtc}</span>
                    <button
                      onClick={() => onToggleResolve(alert.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: alert.resolved ? 'var(--color-bullish)' : 'var(--text-secondary)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '0.75rem',
                      }}
                    >
                      <CheckCircle2 size={14} />
                      {alert.resolved ? 'Resolved' : 'Mark Resolved'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
